import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge } from "@cx/ui";
import { FISCAL_DOCUMENT_STATUSES, type FiscalDocumentList, type FiscalDocumentStatus } from "@cx/contracts";
import { formatBRL, formatDateBR, formatDocument, todayInSaoPaulo } from "@cx/domain";
import { useSession } from "../../session";
import { ENVIRONMENT_LABEL, INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE, isInFlight } from "./labels";

/** Vendas › Notas fiscais (T2.3, RF-VEN-03): NF-e de saída com situação, destinatário, valor e origem. */
export function InvoicesListPage(): ReactElement {
  const { companyId, api } = useSession();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<FiscalDocumentStatus | "">("");
  const term = q.trim();
  const list = useInfiniteQuery({
    queryKey: ["fiscal-documents", companyId, "list", term, status],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<FiscalDocumentList>(
        `/fiscal-documents?${new URLSearchParams({
          ...(term ? { q: term } : {}),
          ...(status ? { status } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // Nota em envio muda sozinha: a lista acompanha até a SEFAZ responder.
    refetchInterval: (query) =>
      query.state.data?.pages.some((p) => p.data.some((d) => isInFlight(d.status))) ? 3000 : false,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const summary = list.data?.pages[0]?.summary;
  return (
    <>
      <ContentHeader title="Notas fiscais" breadcrumb={["Vendas", "Notas fiscais"]} />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-5">
            <label className="visually-hidden" htmlFor="invoices-q">
              Pesquisar nota
            </label>
            <input
              id="invoices-q"
              type="search"
              className="form-control"
              placeholder="Pesquisar por número, chave ou destinatário"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="visually-hidden" htmlFor="invoices-status">
              Situação
            </label>
            <select
              id="invoices-status"
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value as FiscalDocumentStatus | "")}
            >
              <option value="">Todas as situações</option>
              {FISCAL_DOCUMENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {INVOICE_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhuma nota fiscal"
            description="Emita a NF-e pelo pedido de venda (botão Emitir NF-e). A nota autorizada fica aqui com o DANFE e o XML."
            actions={[]}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Notas fiscais">
                <thead>
                  <tr>
                    <th scope="col">Número</th>
                    <th scope="col">Emissão</th>
                    <th scope="col">Destinatário</th>
                    <th scope="col">Origem</th>
                    <th scope="col" className="text-end">
                      Valor
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="text-nowrap">
                        <Link to="/vendas/notas-fiscais/$id" params={{ id: r.id }}>
                          {r.number === null ? "Rascunho" : `${r.number} / ${r.series ?? 1}`}
                        </Link>
                        {r.environment === 2 && (
                          <span className="badge text-bg-warning ms-2" title="Sem valor fiscal">
                            {ENVIRONMENT_LABEL[2]}
                          </span>
                        )}
                      </td>
                      <td>{formatDateBR(todayInSaoPaulo(new Date(r.issuedAt ?? r.createdAt)))}</td>
                      <td>
                        {r.recipientName}
                        {r.recipientDocument && (
                          <span className="text-muted small ms-2">{formatDocument(r.recipientDocument)}</span>
                        )}
                      </td>
                      <td>
                        {r.origin?.type === "grouped_billing" ? (
                          <Link to="/financas/faturamento-agrupado/$id" params={{ id: r.origin.id }}>
                            Faturamento agrupado nº {r.origin.number ?? "—"}
                          </Link>
                        ) : r.origin?.type === "service_order" ? (
                          <Link to="/servicos/ordens-de-servico/$id" params={{ id: r.origin.id }}>
                            OS nº {r.origin.number}
                          </Link>
                        ) : r.origin?.type === "fiscal_document" ? (
                          <Link to="/vendas/notas-fiscais/$id" params={{ id: r.origin.id }}>
                            Devolução da NF-e nº {r.origin.number ?? "—"}
                          </Link>
                        ) : r.origin ? (
                          <Link to="/vendas/pedidos/$id" params={{ id: r.origin.id }}>
                            Pedido nº {r.origin.number ?? "—"}
                          </Link>
                        ) : (
                          "Avulsa"
                        )}
                      </td>
                      <td className="text-end">{formatBRL(r.totalCents)}</td>
                      <td>
                        <StatusBadge tone={INVOICE_STATUS_TONE[r.status]}>
                          {INVOICE_STATUS_LABEL[r.status]}
                        </StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
              {summary && (
                <span className="text-muted small" data-testid="invoices-summary">
                  {summary.count} {summary.count === 1 ? "nota" : "notas"}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
